import { useCallback, useEffect, useMemo, useState } from 'react';
import { Field, Form, FormSpy } from 'react-final-form';
import { useTranslation } from 'react-i18next';
import { Box, FormHelperText, Grid2, InputLabel, styled } from '@mui/material';
import { getRatesOptions, Price } from '@utriper/core';
import { Autocomplete, SelectComponent } from '@utriper/core-ui';
import { usePriceGroup } from 'core/contexts/priceGroup';
import Title from '../../../components/Title';
import ActionButtons from '../ActionButtons';
import PriceGroupGrid from 'modules/Pricing/components/PriceGroupGrid';

const Container = ({ children, ...props }) => {
    return (
        <Grid2 container width="100%" direction="column" justifyContent="flex-start" alignItems="flex-start" gap="4px" {...props}>
            {children}
        </Grid2>
    );
};

const Label = styled(InputLabel)({
    fontSize: '18px',
    fontWeight: 400,
    color: '#131313',
});

const PriceForm = () => {
    const { priceGroupData, priceForm, units, unitsForm, setPriceForm, addPriceGroup, submitting, setSuccess, handleBack, validatePrices } = usePriceGroup();
    const { i18n, t } = useTranslation();
    const translate = (key) => t(`price_group_wizard.price_form.${key}`);
    const [submitted, setSubmitted] = useState(false);

    const priceTypeOptions = useMemo(
        () =>
            Price.getIds().map((priceType) => ({
                id: priceType,
                name: Price.getTranslatedName(Price.types, priceType, i18n.language),
            })),
        [i18n.language]
    );
    const currencyOptions = getRatesOptions();

    const unitsValues = useMemo(() => {
        if (Array.isArray(unitsForm?.unitsIds) && unitsForm?.unitsIds?.length > 0) {
            const unitsIds = [];

            for (const unit of unitsForm?.unitsIds) {
                const unitValues = units.find((u) => u?.typeId === unit?.typeId)?.values ?? [];
                const unitData = unitValues.find((u) => u?.id === unit?.unitId);

                if (unitData?.id) {
                    unitsIds.push({ id: unitData?.id, name: unitData?.name });
                }
            }

            return unitsIds;
        }

        return [];
    }, [unitsForm, units]);

    useEffect(() => {
        const { priceRanges = [] } = priceForm;

        if (priceRanges?.some((priceRange) => !priceRange?.hasOwnProperty('rowId'))) {
            const pricesWithRowId = priceRanges.map((priceRange, index) => (!priceRange?.hasOwnProperty('rowId') ? { ...priceRange, rowId: index } : priceRange));

            setPriceForm((prevPriceForm) => ({ ...prevPriceForm, priceRanges: pricesWithRowId }));
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [priceForm.priceRanges]);

    const validateForm = (values) => {
        const errors = {};

        if (!Number.isInteger(values?.priceType)) {
            errors.priceType = translate('validate_form');
        }

        if (!values?.currency) {
            errors.currency = translate('validate_form');
        }

        const priceErrors = validatePrices(values?.priceRanges, unitsValues);

        errors.priceRanges = priceErrors;

        return errors;
    };

    const handleFormSubmit = useCallback(async () => {
        const result = await addPriceGroup();
        if (result) {
            setSuccess(result);
        }
    }, [addPriceGroup, setSuccess]);

    const handleSetPrices = (updater) => {
        setPriceForm((prevForm) => ({
            ...prevForm,
            priceRanges: updater(prevForm?.priceRanges ?? []),
        }));
    };

    return (
        <Form
            onSubmit={handleFormSubmit}
            initialValues={priceForm}
            validate={validateForm}
            render={({ errors, handleSubmit }) => {
                const hasRangeErrors = submitted && errors?.priceRanges;
                const rangeErrors = errors?.priceRanges ?? [];

                return (
                    <form onSubmit={handleSubmit} noValidate style={{ width: '100%', maxWidth: '480px' }}>
                        <Box component="section">
                            <Title title={translate('title')} description={translate('description')} />

                            {/* Form */}
                            <Grid2 container width="100%" direction="column" justifyContent="flex-start" alignItems="flex-start" gap="8px" mt="24px">
                                {/* Price Type */}
                                <Container>
                                    <Label>{translate('form.price_type')}</Label>
                                    <Field name="priceType" component={SelectComponent} values={priceTypeOptions} displayEmpty label={translate('form.price_type')} hasError={submitted && errors?.priceType} errorMessage={errors?.priceType} disabled={submitting} />
                                </Container>

                                {/* Currency */}
                                <Container>
                                    <Label>{translate('form.currency')}</Label>
                                    <Field name="currency" component={Autocomplete} options={currencyOptions} label={translate('form.currency')} fullWidth disabled={submitting} />
                                </Container>

                                {/* Prices Grid */}
                                <Grid2 size={12} mt={2}>
                                    <Box
                                        sx={{
                                            width: '100%',
                                            minHeight: 'fit-content',
                                            overflowY: 'auto',
                                            border: '1px solid #ddd',
                                            borderRadius: 2,
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'center',
                                        }}
                                    >
                                        <Box width="100%">
                                            <PriceGroupGrid
                                                data={{
                                                    units: unitsValues,
                                                    bookingOrigins: Array.isArray(priceGroupData?.bookingOrigins) ? priceGroupData?.bookingOrigins?.map((bkgOrigin) => bkgOrigin?.id) ?? [] : [],
                                                    prices: priceForm?.priceRanges,
                                                }}
                                                actions={{
                                                    setPrices: handleSetPrices,
                                                }}
                                            />
                                        </Box>
                                    </Box>
                                    {/* Errors Ranges */}
                                    <Box
                                        sx={{
                                            width: '100%',
                                            maxHeight: '78px',
                                            overflowY: 'auto',
                                            mt: 2,
                                        }}
                                    >
                                        {hasRangeErrors && rangeErrors?.length > 0 && rangeErrors.map((error) => <FormHelperText error={hasRangeErrors}>{error}</FormHelperText>)}
                                    </Box>
                                </Grid2>
                            </Grid2>

                            <ActionButtons
                                handleSubmit={(e) => {
                                    setSubmitted(true);
                                    return handleSubmit(e);
                                }}
                                handleBack={handleBack}
                                submitting={submitting}
                            />
                        </Box>

                        {/* Form Spy */}
                        <FormSpy
                            subscription={{ values: true }}
                            onChange={({ values }) => {
                                setPriceForm((prev) => ({ ...prev, priceType: values?.priceType, currency: values?.currency }));
                            }}
                        />
                    </form>
                );
            }}
        />
    );
};

export default PriceForm;
